/**
 * Разбор старых бинарных .xls (BIFF8, Excel 97-2003): поток `Workbook` из OLE2-контейнера
 * состоит из записей `(тип, длина, данные)`. Читаем только то, что нужно для просмотра:
 * листы, значения ячеек, числовые форматы, оформление, объединения, размеры строк и столбцов.
 * Формулы не вычисляются — берутся закешированные Excel'ем значения.
 */
import { readCfbStreams } from './cfb';
import { builtinFormat, formatNumber, formatText } from './format';
import { INDEXED_PALETTE, indexedColor, rgbToHex } from './colors';
import {
  emptyCell,
  type BorderEdge,
  type BorderStyle,
  type CellBorders,
  type CellStyle,
  type FontStyle,
  type HorizontalAlign,
  type Sheet,
  type SheetCell,
  type SheetMerge,
  type TextRun,
  type VerticalAlign,
} from './types';

const RECORD = {
  FORMULA: 0x0006,
  EOF: 0x000a,
  DATEMODE: 0x0022,
  PANE: 0x0041,
  DEFCOLWIDTH: 0x0055,
  BOUNDSHEET: 0x0085,
  STANDARDWIDTH: 0x0099,
  SCL: 0x00a0,
  CONTINUE: 0x003c,
  COLINFO: 0x007d,
  MULRK: 0x00bd,
  MULBLANK: 0x00be,
  XF: 0x00e0,
  MERGEDCELLS: 0x00e5,
  SST: 0x00fc,
  LABELSST: 0x00fd,
  BLANK: 0x0201,
  NUMBER: 0x0203,
  LABEL: 0x0204,
  BOOLERR: 0x0205,
  STRING: 0x0207,
  ROW: 0x0208,
  DEFAULTROWHEIGHT: 0x0225,
  WINDOW2: 0x023e,
  RK: 0x027e,
  FORMAT: 0x041e,
  FONT: 0x0031,
  PALETTE: 0x0092,
  BOF: 0x0809,
};

const ERRORS: Record<number, string> = {
  0x00: '#ПУСТО!',
  0x07: '#ДЕЛ/0!',
  0x0f: '#ЗНАЧ!',
  0x17: '#ССЫЛКА!',
  0x1d: '#ИМЯ?',
  0x24: '#ЧИСЛО!',
  0x2a: '#Н/Д',
};

const BORDER_STYLES: (BorderStyle | undefined)[] = [
  undefined,
  'thin',
  'medium',
  'dashed',
  'dotted',
  'thick',
  'double',
  'hair',
  'mediumDashed',
  'dashDot',
  'mediumDashDot',
  'dashDotDot',
  'mediumDashDotDot',
  'slantDashDot',
];

interface BiffRecord {
  type: number;
  start: number;
  data: Uint8Array;
}

function readRecords(stream: Uint8Array): BiffRecord[] {
  const view = new DataView(stream.buffer, stream.byteOffset, stream.byteLength);
  const records: BiffRecord[] = [];
  let offset = 0;
  while (offset + 4 <= stream.length) {
    const type = view.getUint16(offset, true);
    const size = view.getUint16(offset + 2, true);
    if (offset + 4 + size > stream.length) {
      break;
    }
    records.push({ type, start: offset, data: stream.subarray(offset + 4, offset + 4 + size) });
    offset += 4 + size;
  }
  return records;
}

function viewOf(data: Uint8Array): DataView {
  return new DataView(data.buffer, data.byteOffset, data.byteLength);
}

function decodeChars(data: Uint8Array, offset: number, count: number, highByte: boolean): string {
  if (highByte) {
    return new TextDecoder('utf-16le').decode(data.subarray(offset, offset + count * 2));
  }
  // сжатая строка: байт = код символа UTF-16, а не кодировка windows-1252
  let text = '';
  for (let i = 0; i < count; i++) {
    text += String.fromCharCode(data[offset + i]);
  }
  return text;
}

/** XLUnicodeString: длина в 1 или 2 байта, затем флаги и символы (1 или 2 байта на символ). */
function readUnicodeString(data: Uint8Array, offset: number, lengthBytes: 1 | 2): { text: string; next: number } {
  const view = viewOf(data);
  const charCount = lengthBytes === 1 ? data[offset] : view.getUint16(offset, true);
  let pos = offset + lengthBytes;
  const flags = data[pos];
  pos += 1;
  const highByte = Boolean(flags & 0x01);
  const runCount = flags & 0x08 ? view.getUint16(pos, true) : 0;
  if (flags & 0x08) {
    pos += 2;
  }
  const extendedSize = flags & 0x04 ? view.getUint32(pos, true) : 0;
  if (flags & 0x04) {
    pos += 4;
  }

  const text = decodeChars(data, pos, charCount, highByte);
  pos += charCount * (highByte ? 2 : 1) + runCount * 4 + extendedSize;
  return { text, next: pos };
}

interface BiffString {
  text: string;
  runs?: { start: number; font: number }[];
}

/**
 * SST (общая таблица строк) может занимать несколько записей: продолжение начинается
 * с байта флагов, задающего кодировку остатка разорванной строки.
 */
function parseSharedStrings(blocks: Uint8Array[]): BiffString[] {
  if (!blocks.length) {
    return [];
  }
  const strings: BiffString[] = [];
  let block = 0;
  let offset = 8; // общее число строк + число уникальных строк
  const total = viewOf(blocks[0]).getUint32(4, true);

  const nextBlock = (readFlags: boolean): boolean => {
    if (block + 1 >= blocks.length) {
      return false;
    }
    block += 1;
    offset = readFlags ? 1 : 0;
    return true;
  };

  while (strings.length < total && block < blocks.length) {
    // заголовок строки не разрывается: хвост блока меньше 4 байт — просто добивка
    if (offset + 3 > blocks[block].length && !nextBlock(false)) {
      break;
    }

    const view = viewOf(blocks[block]);
    let remaining = view.getUint16(offset, true);
    let flags = blocks[block][offset + 2];
    offset += 3;
    let highByte = Boolean(flags & 0x01);
    const runCount = flags & 0x08 ? view.getUint16(offset, true) : 0;
    if (flags & 0x08) {
      offset += 2;
    }
    const extendedSize = flags & 0x04 ? view.getUint32(offset, true) : 0;
    if (flags & 0x04) {
      offset += 4;
    }

    let text = '';
    while (remaining > 0) {
      const charSize = highByte ? 2 : 1;
      const available = Math.floor((blocks[block].length - offset) / charSize);
      const take = Math.min(available, remaining);
      if (take > 0) {
        text += decodeChars(blocks[block], offset, take, highByte);
        offset += take * charSize;
        remaining -= take;
      }
      if (remaining > 0) {
        if (!nextBlock(true)) {
          break;
        }
        flags = blocks[block][0];
        highByte = Boolean(flags & 0x01);
      }
    }

    const runBytes: number[] = [];
    let skip = runCount * 4 + extendedSize;
    while (skip > 0) {
      const available = blocks[block].length - offset;
      const take = Math.min(available, skip);
      for (let i = 0; i < take && runBytes.length < runCount * 4; i++) {
        runBytes.push(blocks[block][offset + i]);
      }
      offset += take;
      skip -= take;
      if (skip > 0 && !nextBlock(false)) {
        break;
      }
    }

    const runs: BiffString['runs'] = [];
    for (let i = 0; i + 3 < runBytes.length; i += 4) {
      runs.push({ start: runBytes[i] | (runBytes[i + 1] << 8), font: runBytes[i + 2] | (runBytes[i + 3] << 8) });
    }

    strings.push(runs.length ? { text, runs } : { text });
  }
  return strings;
}

/** RK — упакованное число: целое или старшие 32 бита double, опционально делённое на 100. */
function rkToNumber(raw: number): number {
  let value: number;
  if (raw & 0x02) {
    value = (raw | 0) >> 2;
  } else {
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setUint32(4, raw & 0xfffffffc, true);
    value = view.getFloat64(0, true);
  }
  return raw & 0x01 ? value / 100 : value;
}

interface RawFont {
  font: FontStyle;
  colorIndex: number;
}

interface Globals {
  sharedStrings: BiffString[];
  cellFormats: (string | undefined)[];
  cellStyles: CellStyle[];
  fonts: FontStyle[];
  sheets: { name: string; position: number }[];
  date1904: boolean;
}

const HORIZONTAL_ALIGN: Record<number, HorizontalAlign> = {
  0: 'general',
  1: 'left',
  2: 'center',
  3: 'right',
  4: 'fill',
  5: 'justify',
  6: 'centerContinuous',
  7: 'distributed',
};
const VERTICAL_ALIGN: Record<number, VerticalAlign> = {
  0: 'top',
  1: 'middle',
  2: 'bottom',
  3: 'justify',
  4: 'distributed',
};

/** FONT: высота в твипах, начертание, цвет — индексом в палитре. */
function readFont(data: Uint8Array): RawFont {
  const view = viewOf(data);
  const flags = view.getUint16(2, true);
  const underline = data[10];
  const script = view.getUint16(8, true);
  const font: FontStyle = {
    fontSize: view.getUint16(0, true) / 20,
    italic: Boolean(flags & 0x02) || undefined,
    strike: Boolean(flags & 0x08) || undefined,
    bold: view.getUint16(6, true) >= 700 || undefined,
    underline: underline === 0 ? undefined : underline === 2 || underline === 0x22 ? 'double' : 'single',
    vertAlign: script === 1 ? 'superscript' : script === 2 ? 'subscript' : undefined,
  };
  if (data.length > 15) {
    font.fontName = readUnicodeString(data, 14, 1).text;
  }
  return { font, colorIndex: view.getUint16(4, true) };
}

/** PALETTE переопределяет цвета начиная с индекса 8. */
function readPalette(data: Uint8Array): string[] {
  const view = viewOf(data);
  const palette = [...INDEXED_PALETTE];
  const count = view.getUint16(0, true);
  for (let i = 0; i < count && 2 + i * 4 + 4 <= data.length; i++) {
    const offset = 2 + i * 4;
    palette[8 + i] = rgbToHex(data[offset], data[offset + 1], data[offset + 2]);
  }
  return palette;
}

function edge(style: number, colorIndex: number, palette: string[]): BorderEdge | undefined {
  const name = BORDER_STYLES[style];
  return name ? { style: name, color: indexedColor(colorIndex, palette) } : undefined;
}

/** XF: ссылка на шрифт и числовой формат, выравнивание, границы и индексы цветов заливки. */
function readXfStyle(data: Uint8Array, fonts: FontStyle[], palette: string[]): CellStyle {
  const view = viewOf(data);
  const fontIndex = view.getUint16(0, true);
  // индекс 4 в таблице шрифтов не используется — Excel хранит их со сдвигом
  const font = fonts[fontIndex >= 4 ? fontIndex - 1 : fontIndex] ?? {};

  const alignment = data[6];
  const rotation = data[7];
  const indent = data[8] & 0x0f;
  const shrink = Boolean(data[8] & 0x10);
  const result: CellStyle = {
    ...font,
    align: HORIZONTAL_ALIGN[alignment & 0x07],
    valign: VERTICAL_ALIGN[(alignment >> 4) & 0x07],
    wrap: Boolean(alignment & 0x08) || undefined,
    rotation: rotation || undefined,
    indent: indent || undefined,
    shrink: shrink || undefined,
  };

  if (data.length >= 20) {
    const lines = view.getUint32(10, true);
    const colors = view.getUint32(14, true);
    const fill = view.getUint16(18, true);
    const borders: CellBorders = {
      left: edge(lines & 0x0f, (lines >>> 16) & 0x7f, palette),
      right: edge((lines >>> 4) & 0x0f, (lines >>> 23) & 0x7f, palette),
      top: edge((lines >>> 8) & 0x0f, colors & 0x7f, palette),
      bottom: edge((lines >>> 12) & 0x0f, (colors >>> 7) & 0x7f, palette),
    };
    const diagonalKind = (lines >>> 30) & 0x03;
    const diagonal = edge((colors >>> 21) & 0x0f, (colors >>> 14) & 0x7f, palette);
    if (diagonal && diagonalKind & 0x01) {
      borders.diagonalDown = diagonal;
    }
    if (diagonal && diagonalKind & 0x02) {
      borders.diagonalUp = diagonal;
    }
    if (Object.values(borders).some(Boolean)) {
      result.borders = borders;
    }

    const pattern = colors >>> 26;
    if (pattern) {
      result.background = indexedColor(fill & 0x7f, palette);
    }
  }

  Object.keys(result).forEach((key) => {
    if ((result as Record<string, unknown>)[key] === undefined) {
      delete (result as Record<string, unknown>)[key];
    }
  });
  return result;
}

function parseGlobals(records: BiffRecord[]): Globals {
  const sheets: Globals['sheets'] = [];
  const formatCodes = new Map<number, string>();
  const xfRecords: Uint8Array[] = [];
  const rawFonts: RawFont[] = [];
  let palette = INDEXED_PALETTE;
  let sharedStrings: BiffString[] = [];
  let date1904 = false;

  for (let i = 0; i < records.length; i++) {
    const record = records[i];
    if (record.type === RECORD.EOF) {
      break;
    }

    if (record.type === RECORD.BOUNDSHEET) {
      const position = viewOf(record.data).getUint32(0, true);
      const hidden = (record.data[4] & 0x03) !== 0;
      const isWorksheet = record.data[5] === 0;
      if (!hidden && isWorksheet) {
        sheets.push({ name: readUnicodeString(record.data, 6, 1).text, position });
      }
    } else if (record.type === RECORD.FORMAT) {
      const formatId = viewOf(record.data).getUint16(0, true);
      formatCodes.set(formatId, readUnicodeString(record.data, 2, 2).text);
    } else if (record.type === RECORD.XF) {
      xfRecords.push(record.data);
    } else if (record.type === RECORD.FONT) {
      rawFonts.push(readFont(record.data));
    } else if (record.type === RECORD.PALETTE) {
      palette = readPalette(record.data);
    } else if (record.type === RECORD.DATEMODE) {
      date1904 = viewOf(record.data).getUint16(0, true) === 1;
    } else if (record.type === RECORD.SST) {
      const blocks = [record.data];
      for (let j = i + 1; j < records.length && records[j].type === RECORD.CONTINUE; j++) {
        blocks.push(records[j].data);
      }
      sharedStrings = parseSharedStrings(blocks);
    }
  }

  const fonts = rawFonts.map(({ font, colorIndex }) => {
    const color = indexedColor(colorIndex, palette);
    return color && colorIndex !== 0x7fff ? { ...font, color } : font;
  });

  const cellFormats = xfRecords.map((data) => {
    const formatId = viewOf(data).getUint16(2, true);
    return formatCodes.get(formatId) ?? builtinFormat(formatId);
  });
  const cellStyles = xfRecords.map((data) => readXfStyle(data, fonts, palette));

  return { sharedStrings, cellFormats, cellStyles, fonts, sheets, date1904 };
}

class SheetBuilder {
  private rows: SheetCell[][] = [];
  maxColumn = 0;
  merges: SheetMerge[] = [];
  columnWidths: (number | undefined)[] = [];
  hiddenColumns: boolean[] = [];
  rowHeights: (number | undefined)[] = [];
  hiddenRows: boolean[] = [];
  defaultColumnWidth: number | undefined;
  baseColumnWidth = 8;
  defaultRowHeight: number | undefined;
  showGridLines = true;
  frozen = false;
  frozenRows = 0;
  frozenColumns = 0;
  zoom = 1;
  date1904 = false;

  constructor(private styles: CellStyle[]) {}

  /** xf — индекс записи XF: из неё берётся оформление ячейки. */
  set(row: number, column: number, cell: SheetCell, xf?: number): void {
    while (this.rows.length <= row) {
      this.rows.push([]);
    }
    const cells = this.rows[row];
    while (cells.length < column) {
      cells.push(emptyCell());
    }
    const style = xf === undefined ? undefined : this.styles[xf];
    cells[column] = style ? { ...cell, style } : cell;
    this.maxColumn = Math.max(this.maxColumn, column + 1);
  }

  build(name: string, defaultFont: FontStyle): Sheet {
    if (this.rows.length * this.maxColumn <= 2_000_000) {
      this.rows.forEach((cells) => {
        while (cells.length < this.maxColumn) {
          cells.push(emptyCell());
        }
      });
    }
    return {
      name,
      rows: this.rows,
      merges: this.merges,
      columnWidths: this.columnWidths,
      hiddenColumns: this.hiddenColumns,
      rowHeights: this.rowHeights,
      hiddenRows: this.hiddenRows,
      defaultColumnWidth: this.defaultColumnWidth,
      baseColumnWidth: this.baseColumnWidth,
      defaultRowHeight: this.defaultRowHeight,
      showGridLines: this.showGridLines,
      frozenRows: this.frozen ? this.frozenRows : 0,
      frozenColumns: this.frozen ? this.frozenColumns : 0,
      zoom: this.zoom,
      images: [],
      defaultFont,
      date1904: this.date1904,
    };
  }
}

function sharedToCell(shared: BiffString, fonts: FontStyle[]): SheetCell {
  const cell: SheetCell = { kind: shared.text ? 'text' : 'empty', text: shared.text, value: shared.text };
  if (!shared.runs?.length) {
    return cell;
  }
  const runs: TextRun[] = [];
  if (shared.runs[0].start > 0) {
    runs.push({ text: shared.text.slice(0, shared.runs[0].start) });
  }
  shared.runs.forEach((run, index) => {
    const end = shared.runs[index + 1]?.start ?? shared.text.length;
    const font = fonts[run.font >= 4 ? run.font - 1 : run.font];
    runs.push({ text: shared.text.slice(run.start, end), font });
  });
  cell.runs = runs;
  return cell;
}

function parseSheet(records: BiffRecord[], from: number, globals: Globals, name: string): Sheet {
  const builder = new SheetBuilder(globals.cellStyles);
  builder.date1904 = globals.date1904;
  const formatOf = (xf: number) => globals.cellFormats[xf];
  const numberCell = (value: number, xf: number): SheetCell => {
    const formatCode = formatOf(xf);
    const formatted = formatNumber(value, formatCode, globals.date1904);
    return {
      kind: 'number',
      text: formatted.text,
      value,
      formatCode,
      formatColor: formatted.color,
      fill: formatted.fill,
    };
  };
  const textCell = (text: string, xf: number): SheetCell => {
    const formatted = formatText(text, formatOf(xf));
    return { kind: 'text', text: formatted.text, value: text, formatColor: formatted.color, fill: formatted.fill };
  };
  // FORMULA со строковым результатом хранит его в следующей записи STRING
  let pendingString: { row: number; column: number; xf: number } | null = null;

  for (let i = from; i < records.length; i++) {
    const { type, data } = records[i];
    if (type === RECORD.EOF) {
      break;
    }
    const view = viewOf(data);

    switch (type) {
      case RECORD.LABELSST: {
        const shared = globals.sharedStrings[view.getUint32(6, true)] ?? { text: '' };
        builder.set(
          view.getUint16(0, true),
          view.getUint16(2, true),
          sharedToCell(shared, globals.fonts),
          view.getUint16(4, true),
        );
        break;
      }
      case RECORD.LABEL: {
        const xf = view.getUint16(4, true);
        builder.set(
          view.getUint16(0, true),
          view.getUint16(2, true),
          textCell(readUnicodeString(data, 6, 2).text, xf),
          xf,
        );
        break;
      }
      case RECORD.RK: {
        const xf = view.getUint16(4, true);
        builder.set(
          view.getUint16(0, true),
          view.getUint16(2, true),
          numberCell(rkToNumber(view.getUint32(6, true)), xf),
          xf,
        );
        break;
      }
      case RECORD.MULRK: {
        const row = view.getUint16(0, true);
        const firstColumn = view.getUint16(2, true);
        for (let offset = 4, column = firstColumn; offset + 6 <= data.length - 2; offset += 6, column += 1) {
          const xf = view.getUint16(offset, true);
          builder.set(row, column, numberCell(rkToNumber(view.getUint32(offset + 2, true)), xf), xf);
        }
        break;
      }
      case RECORD.NUMBER: {
        const xf = view.getUint16(4, true);
        builder.set(view.getUint16(0, true), view.getUint16(2, true), numberCell(view.getFloat64(6, true), xf), xf);
        break;
      }
      case RECORD.BOOLERR: {
        const row = view.getUint16(0, true);
        const column = view.getUint16(2, true);
        const xf = view.getUint16(4, true);
        if (data[7]) {
          builder.set(row, column, { kind: 'error', text: ERRORS[data[6]] ?? '#Н/Д' }, xf);
        } else {
          builder.set(row, column, { kind: 'boolean', text: data[6] ? 'ИСТИНА' : 'ЛОЖЬ', value: Boolean(data[6]) }, xf);
        }
        break;
      }
      case RECORD.FORMULA: {
        const row = view.getUint16(0, true);
        const column = view.getUint16(2, true);
        const xf = view.getUint16(4, true);
        // маркер 0xFFFF в старших байтах: значение не число, а строка/логическое/ошибка
        if (view.getUint16(12, true) === 0xffff) {
          const kind = data[6];
          if (kind === 0) {
            pendingString = { row, column, xf };
          } else if (kind === 1) {
            builder.set(
              row,
              column,
              { kind: 'boolean', text: data[8] ? 'ИСТИНА' : 'ЛОЖЬ', value: Boolean(data[8]) },
              xf,
            );
          } else if (kind === 2) {
            builder.set(row, column, { kind: 'error', text: ERRORS[data[8]] ?? '#ЗНАЧ!' }, xf);
          } else {
            builder.set(row, column, emptyCell(), xf);
          }
        } else {
          builder.set(row, column, numberCell(view.getFloat64(6, true), xf), xf);
        }
        break;
      }
      case RECORD.STRING: {
        if (pendingString) {
          builder.set(
            pendingString.row,
            pendingString.column,
            textCell(readUnicodeString(data, 0, 2).text, pendingString.xf),
            pendingString.xf,
          );
          pendingString = null;
        }
        break;
      }
      case RECORD.BLANK: {
        builder.set(view.getUint16(0, true), view.getUint16(2, true), emptyCell(), view.getUint16(4, true));
        break;
      }
      case RECORD.MULBLANK: {
        const row = view.getUint16(0, true);
        const firstColumn = view.getUint16(2, true);
        const count = Math.max(Math.floor((data.length - 6) / 2), 0);
        for (let j = 0; j < count; j++) {
          builder.set(row, firstColumn + j, emptyCell(), view.getUint16(4 + j * 2, true));
        }
        break;
      }
      case RECORD.MERGEDCELLS: {
        const count = view.getUint16(0, true);
        for (let j = 0; j < count; j++) {
          const offset = 2 + j * 8;
          builder.merges.push({
            rowFrom: view.getUint16(offset, true),
            rowTo: view.getUint16(offset + 2, true),
            colFrom: view.getUint16(offset + 4, true),
            colTo: view.getUint16(offset + 6, true),
          });
        }
        break;
      }
      case RECORD.COLINFO: {
        const firstColumn = view.getUint16(0, true);
        const lastColumn = view.getUint16(2, true);
        const width = view.getUint16(4, true) / 256;
        const hidden = Boolean(view.getUint16(8, true) & 0x01);
        for (let column = firstColumn; column <= lastColumn && column < 256; column++) {
          builder.columnWidths[column] = width;
          if (hidden) {
            builder.hiddenColumns[column] = true;
          }
        }
        break;
      }
      case RECORD.ROW: {
        const row = view.getUint16(0, true);
        const height = view.getUint16(6, true);
        const flags = view.getUint16(12, true);
        if (!(height & 0x8000)) {
          builder.rowHeights[row] = (height & 0x7fff) / 20;
        }
        if (flags & 0x20) {
          builder.hiddenRows[row] = true;
        }
        break;
      }
      case RECORD.DEFAULTROWHEIGHT: {
        if (data.length >= 4) {
          builder.defaultRowHeight = view.getUint16(2, true) / 20;
        }
        break;
      }
      case RECORD.DEFCOLWIDTH: {
        builder.baseColumnWidth = view.getUint16(0, true);
        break;
      }
      case RECORD.STANDARDWIDTH: {
        builder.defaultColumnWidth = view.getUint16(0, true) / 256;
        break;
      }
      case RECORD.WINDOW2: {
        const flags = view.getUint16(0, true);
        builder.showGridLines = Boolean(flags & 0x0002);
        builder.frozen = Boolean(flags & 0x0008);
        break;
      }
      case RECORD.PANE: {
        builder.frozenColumns = view.getUint16(0, true);
        builder.frozenRows = view.getUint16(2, true);
        break;
      }
      case RECORD.SCL: {
        const denominator = view.getUint16(2, true);
        if (denominator) {
          builder.zoom = view.getUint16(0, true) / denominator;
        }
        break;
      }
    }
  }

  return builder.build(name, { fontName: 'Arial', fontSize: 10, ...(globals.fonts[0] ?? {}) });
}

export function parseXls(bytes: Uint8Array): Sheet[] {
  const streams = readCfbStreams(bytes);
  const workbook = streams.get('Workbook') ?? streams.get('Book');
  if (!workbook) {
    throw new Error('В файле нет потока Workbook');
  }

  const records = readRecords(workbook);
  const globals = parseGlobals(records);
  if (!globals.sheets.length) {
    throw new Error('В файле не найдено ни одного листа');
  }

  return globals.sheets.map(({ name, position }, index) => {
    const start = records.findIndex((record) => record.start === position && record.type === RECORD.BOF);
    return parseSheet(records, start >= 0 ? start + 1 : 0, globals, name || `Лист ${index + 1}`);
  });
}
